<template>
  <div class="col-sm-12 pd-5">
    <div class="card p-1 mb-0 shadow-dashboard">
      <div class="card-header bg-white d-flex justify-content-between align-items-center py-2 border-bottom">
        <h5 class="font-bold mb-0">Donor KPI (Top 5)</h5>
        <div class="d-flex align-items-center">
          <button @click="printReport" class="btn btn-sm btn-outline-secondary">
             <i class="feather icon-printer mr-25"></i> Print
          </button>
        </div>
      </div>
      
      <div class="card-body p-0">
        <div class="row mt-1 mb-1 px-1 align-items-end">
          <div class="col-md-6 col-lg-5">
            <label class="col-form-label text-left d-block font-bold">Select Project</label>
            <v-select
              placeholder="Select Project"
              :options="projects"
              label="name"
              :reduce="proj => proj.id"
              v-model="projectId"
              @option:selected="fetchData"
              class="v-select-sm"
            />
          </div>
        </div>

        <div v-if="loading" class="text-center py-5">
           <div class="spinner-border text-primary" role="status"></div>
           <p class="mt-1">Loading performance data...</p>
        </div>

        <div v-else-if="projectId && hasData" class="px-1 pb-1">
          <!-- ACTIVITY WISE TABLE ONLY -->
          <div class="table-responsive scrollable-table">
            <table class="table table-sm table-bordered table-striped mb-0">
              <thead>
                <tr class="text-dark bg-white">
                  <th class="text-left py-1 border-bottom-0">Activity</th>
                  <th class="text-right py-1 border-bottom-0">Target</th>
                  <th class="text-right py-1 border-bottom-0">Already Achieved</th>
                  <th class="text-right py-1 border-bottom-0">Balance</th>
                  <th class="text-right py-1 border-bottom-0">Achievement %</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in achievements.activities" :key="item.id" class="align-middle">
                  <td class="activity-name font-bold">{{ item.name }}</td>
                  <td class="text-right">{{ commaFormat(item.total_target) }}</td>
                  <td class="text-right">{{ commaFormat(item.total_achievement) }}</td>
                  <td class="text-right">{{ commaFormat(item.balance) }}</td>
                  <td class="text-right font-bold">{{ item.achievement_percent }}%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="text-right mt-1 pb-1">
            <a @click="seeDetails" class="text-primary font-bold cursor-pointer small">
              See Details.. <i class="feather icon-arrow-right"></i>
            </a>
          </div>
        </div>
        
        <div v-else-if="projectId && !loading" class="text-center py-5">
          <i class="feather icon-alert-circle text-muted mb-1 large-icon"></i>
          <p class="text-muted">No achievements found for this project.</p>
        </div>
        
        <div v-else-if="!loading" class="text-center py-5">
           <div class="mb-2">
             <i class="feather icon-award text-light-primary extra-large-icon"></i>
           </div>
           <h6 class="text-muted">Select a project to see Top 5 achievements</h6>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router'
import handleProjects from "@/services/modules/procurement/project";
import { commaFormat } from "@/services/utils/global";
import useDate from "@/services/utils/day";
import dashboardCardPdfPrinter from "@/services/utils/dashboardCardPdfPrinter";

const { fetchProjects, fetchTopAndLowAchievements } = handleProjects();
const { firstDateOfMonth, lastDateOfMonth } = useDate();

const route = useRoute();
const router = useRouter();
const store = useStore();
const showError = inject('showError');
const { printCardReport } = dashboardCardPdfPrinter();

const projects = ref([]);
const projectId = ref(null);
const achievements = ref({ activities: [], teams: [] });
const loading = ref(false);

const props = defineProps({
  page: Object,
  itemKey: String,
  statisticsItem: Object,
  timeRange: Array
})

const hasData = computed(() => {
  return achievements.value.activities.length > 0;
});

const fetchData = async () => {
  if (!projectId.value) return;
  
  loading.value = true;
  try {
    const query = `?company_id=${route.params.companyId}&start_date=${firstDateOfMonth}&end_date=${lastDateOfMonth}&type=top&project_type=donor_kpi`;
    const res = await fetchTopAndLowAchievements(projectId.value, query);
    
    if (!res.status) {
      achievements.value = { activities: [], teams: [] };
      showError(res.message || 'Failed to fetch data');
      return;
    }

    achievements.value = res.data;
  } catch (error) {
    showError('Something went wrong');
  } finally {
    loading.value = false;
  }
};

const seeDetails = () => {
  const routeData = router.resolve({
    name: 'narrative-report',
    params: { 
      ...route.params, 
      pageId: props.page ? props.page.id : null
    }
  });
  window.open(routeData.href, '_blank');
};

const printReport = () => {
  if (!projectId.value) {
    showError('Please select a project first');
    return;
  }
  
  const selectedProject = projects.value.find(p => p.id === projectId.value);
  const company = store.getters['company/getCompany'];
  
  const headers = [
    { text: 'Activity', width: '*' },
    { text: 'Target', width: 'auto', alignment: 'right' },
    { text: 'Already Achieved', width: 'auto', alignment: 'right' },
    { text: 'Balance', width: 'auto', alignment: 'right' },
    { text: 'Achievement %', width: 'auto', alignment: 'right' }
  ];
  
  const bodyData = achievements.value.activities.map(item => [
    { text: item.name, bold: true },
    { text: commaFormat(item.total_target), alignment: 'right' },
    { text: commaFormat(item.total_achievement), alignment: 'right' },
    { text: commaFormat(item.balance), alignment: 'right' },
    { text: `${item.achievement_percent}%`, alignment: 'right', bold: true }
  ]);
  
  printCardReport(
    'Donor KPI Report',
    headers,
    bodyData,
    company,
    `Project: ${selectedProject.name || ''}`
  );
};

onMounted(async () => {
  loading.value = true;
  try {
    const query = `?company_id=${route.params.companyId}&status=active`;
    const res = await fetchProjects(query);
    if (res.status) projects.value = res.data;
  } catch (error) {
    showError('Failed to load projects');
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.shadow-dashboard {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.05);
}
.font-bold {
  font-weight: 600;
}
.text-primary {
  color: #7367f0 !important;
}
.btn-primary {
  background-color: #7367f0 !important;
  border-color: #7367f0 !important;
}
.btn-outline-primary {
  color: #7367f0 !important;
  border-color: #7367f0 !important;
}
.btn-outline-primary:hover {
  background-color: #7367f0 !important;
  color: #fff !important;
}
.scrollable-table {
  max-height: 400px;
  overflow-y: auto;
}
.activity-name {
  text-align: left;
}
.cursor-pointer {
  cursor: pointer;
}
.text-light-primary {
    color: #e4e2fb;
}
.large-icon {
    font-size: 2rem;
}
.extra-large-icon {
    font-size: 3rem;
    opacity: 0.3;
}
.v-select-sm :deep(.vs__dropdown-toggle) {
  min-height: 32px;
}
.v-select-sm :deep(.vs__selected) {
    font-size: 0.85rem;
}
.border-bottom-0 {
    border-bottom: 0 !important;
}
</style>
